<template>
  <span
    :class="[
      'icon',
      `icon--${size}`,
      { 'icon--clickable': clickable, 'icon--spinning': spin },
      className
    ]"
    :style="iconStyle"
    @click="handleClick"
    v-bind="restAttrs"
  >
    <span v-if="svgContent" class="icon__svg" v-html="processedSvg"></span>
    <slot v-else />
  </span>
</template>

<script setup lang="ts">
import { computed, useAttrs, ref, watch, onMounted } from 'vue';

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface Props {
  // 图标名称（对应 src/assets/icons/ 下的 SVG 文件名，不含扩展名）
  name?: string;
  // 图标尺寸
  size?: Size;
  // 自定义尺寸（优先级高于 size）
  width?: string | number;
  height?: string | number;
  // 图标颜色（使用 CSS color 属性，SVG 会继承）
  color?: string;
  // 是否可点击
  clickable?: boolean;
  // 是否旋转动画
  spin?: boolean;
  // 动画持续时间（秒）
  spinDuration?: number;
  // 自定义类名
  className?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'xs',
  color: 'currentColor',
  clickable: false,
  spin: false,
  spinDuration: 1,
  className: ''
});

const emit = defineEmits<{
  (e: 'click', ev: MouseEvent): void;
}>();

const attrs = useAttrs();
const { class: _c, ...restAttrs } = attrs as Record<string, any>;

const svgContent = ref<string>('');

// 根据图标名称动态加载 SVG 内容
const loadSvgContent = async () => {
  if (!props.name) {
    svgContent.value = '';
    return;
  }

  try {
    // 动态导入 SVG 文件
    const svgModule = await import(`../../../assets/icons/${props.name}.svg?raw`);
    svgContent.value = svgModule.default;
  } catch (error) {
    console.warn(`Failed to load icon: ${props.name}`, error);
    svgContent.value = '';
  }
};

// 处理 SVG 内容：将 fill 属性替换为 currentColor
const processedSvg = computed(() => {
  if (!svgContent.value) return '';

  let processed = svgContent.value;

  // 替换 fill 属性为 currentColor（排除 fill="none"）
  processed = processed.replace(
    /fill="(?!none)[^"]*"/g,
    'fill="currentColor"'
  );

  // 替换内联 style 中的 fill
  processed = processed.replace(
    /style="([^"]*)fill:\s*(?!none)[^;"]*/g,
    (match, prefix) => `style="${prefix}fill: currentColor`
  );

  // 移除 SVG 标签上的固定宽高，让组件控制尺寸
  processed = processed.replace(
    /<svg([^>]*)\s+(width|height)="[^"]*"/g,
    '<svg$1'
  );

  // 确保 SVG 有 fill="currentColor"（如果没有任何 fill）
  if (!/fill=/.test(processed)) {
    processed = processed.replace(
      /<svg/,
      '<svg fill="currentColor"'
    );
  }

  return processed;
});

// 计算图标样式
const iconStyle = computed(() => {
  const style: Record<string, string> = {};

  if (props.width) {
    style.width = typeof props.width === 'number' ? `${props.width}px` : props.width;
    style.height = typeof props.width === 'number' ? `${props.width}px` : props.width;
  }

  if (props.color) {
    style.color = props.color;
  }

  if (props.spin) {
    style['--spin-duration'] = `${props.spinDuration}s`;
  }

  return style;
});

// 点击事件处理
const handleClick = (ev: MouseEvent) => {
  if (props.clickable) {
    emit('click', ev);
  }
};

// 监听 name 变化，重新加载 SVG
watch(() => props.name, loadSvgContent, { immediate: true });

onMounted(() => {
  if (props.name) {
    loadSvgContent();
  }
});
</script>

<style scoped lang="scss">
.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  flex-shrink: 0;
  line-height: 1;
  color: inherit;

  // 尺寸预设
  &--xs {
    width: 14px;
    height: 14px;
  }

  &--sm {
    width: 18px;
    height: 18px;
  }

  &--md {
    width: 20px;
    height: 20px;
  }

  &--lg {
    width: 24px;
    height: 24px;
  }

  &--xl {
    width: 32px;
    height: 32px;
  }

  // 可点击状态
  &--clickable {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }

    &:active {
      opacity: 0.6;
    }
  }

  // 旋转动画
  &--spinning {
    animation: icon-spin var(--spin-duration, 1s) linear infinite;
  }

  // SVG 容器
  .icon__svg {
    display: inline-flex;
    width: 100%;
    height: 100%;

    :deep(svg) {
      width: 100%;
      height: 100%;
      display: block;
    }
  }
}

@keyframes icon-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
